import type { ColumnDef, FilterFn } from '@tanstack/react-table'
import type { ColumnDefinition, ColumnFilterOption } from './types'
import { getColumnLabel } from './types'

export type TableColumnMeta<TRow> = {
  filterAccessor?: (row: TRow) => string | string[]
  sortKey?: string
  fillRemaining?: boolean
  columnLabel?: string
  headerLabelIcon?: ColumnDefinition<TRow>['headerLabelIcon']
}

function getFilterValue<TRow>(
  row: TRow,
  columnId: string,
  filterAccessor?: (row: TRow) => string | string[],
): string | string[] {
  return filterAccessor
    ? filterAccessor(row)
    : String((row as Record<string, unknown>)[columnId] ?? '')
}

function makeCheckboxFilterFn<TRow>(
  columnId: string,
  filterAccessor?: (row: TRow) => string | string[],
): FilterFn<TRow> {
  return (row, _columnId, filterValue) => {
    if (!Array.isArray(filterValue) || filterValue.length === 0) return true
    const v = getFilterValue(row.original, columnId, filterAccessor)
    if (Array.isArray(v)) {
      return v.some((id) => filterValue.includes(id))
    }
    return filterValue.includes(v)
  }
}

function makeSelectFilterFn<TRow>(
  columnId: string,
  filterAccessor?: (row: TRow) => string | string[],
): FilterFn<TRow> {
  return makeCheckboxFilterFn(columnId, filterAccessor)
}

function valueInRange(num: number, opt: ColumnFilterOption): boolean {
  if (opt.min != null && num < opt.min) return false
  if (opt.max != null && num > opt.max) return false
  return true
}

function makeRangeFilterFn<TRow>(
  columnId: string,
  options: ColumnFilterOption[],
  filterAccessor?: (row: TRow) => string | string[],
): FilterFn<TRow> {
  return (row, _columnId, filterValue) => {
    if (!Array.isArray(filterValue) || filterValue.length === 0) return true
    const raw = getFilterValue(row.original, columnId, filterAccessor)
    const num = Number(Array.isArray(raw) ? raw[0] : raw)
    if (Number.isNaN(num)) return false
    return filterValue.some((key) => {
      const opt = options.find((o) => o.value === key)
      return opt ? valueInRange(num, opt) : false
    })
  }
}

export function orderColumnDefinitions<TRow>(
  columns: ColumnDefinition<TRow>[],
  columnOrder: string[],
): ColumnDefinition<TRow>[] {
  const map = new Map(columns.map((c) => [c.id, c]))
  const ordered = columnOrder
    .map((id) => map.get(id))
    .filter((c): c is ColumnDefinition<TRow> => Boolean(c))
  const missing = columns.filter((c) => !columnOrder.includes(c.id))
  return [...ordered, ...missing]
}

/** Search across primitive fields on the row. */
export function globalSearchFilterFn<TData>(): FilterFn<TData> {
  return (row, _columnId, filterValue) => {
    const q = String(filterValue ?? '').trim().toLowerCase()
    if (!q) return true
    return Object.values(row.original as Record<string, unknown>).some((v) =>
      String(v ?? '').toLowerCase().includes(q),
    )
  }
}

function resolveFilterOptions<TRow>(
  col: ColumnDefinition<TRow>,
  rows: TRow[],
): ColumnFilterOption[] {
  if (!col.filterOptions) return []
  return typeof col.filterOptions === 'function'
    ? col.filterOptions(rows)
    : col.filterOptions
}

export interface BuildColumnDefsOptions {
  /** Rightmost visible leaf column — no resize handle on its right edge. */
  rightmostVisibleColumnId?: string
  /** Column that grows/shrinks to fill horizontal space (typically same as rightmost). */
  flexColumnId?: string
}

export function buildDataColumnDefs<TRow extends { id: string }>(
  columns: ColumnDefinition<TRow>[],
  rows: TRow[] = [],
  options?: BuildColumnDefsOptions,
): ColumnDef<TRow>[] {
  const rightmostId = options?.rightmostVisibleColumnId
  const flexId = options?.flexColumnId ?? rightmostId
  return columns.map((col) => {
    const isFlex = col.id === flexId
    const minSize = col.minWidth ?? col.width ?? 150
    const columnLabel = getColumnLabel(col)
    const def: ColumnDef<TRow> = {
      id: col.id,
      accessorKey: col.id,
      header: columnLabel,
      enableSorting: Boolean(col.sortable),
      enableResizing: col.id !== rightmostId,
      size: isFlex ? minSize : (col.width ?? 150),
      minSize: isFlex ? minSize : 60,
      maxSize: 1200,
      cell: ({ row }) => col.render(row.original),
      enableHiding: col.enableHiding !== false,
      meta: {
        filterAccessor: col.filterAccessor,
        sortKey: col.sortKey,
        columnLabel,
        headerLabelIcon: col.headerLabelIcon,
      } satisfies TableColumnMeta<TRow>,
    }

    if (col.sort) {
      def.sortingFn = (rowA, rowB) => col.sort!(rowA.original, rowB.original)
    }

    const filterOpts = resolveFilterOptions(col, rows)
    if (col.filterType === 'checkbox' && filterOpts.length) {
      def.filterFn = makeCheckboxFilterFn<TRow>(col.id, col.filterAccessor)
    } else if (col.filterType === 'select' && filterOpts.length) {
      def.filterFn = makeSelectFilterFn<TRow>(col.id, col.filterAccessor)
    } else if (col.filterType === 'range' && filterOpts.length) {
      def.filterFn = makeRangeFilterFn<TRow>(col.id, filterOpts, col.filterAccessor)
    }

    return def
  })
}

export function getInitialColumnVisibility<TRow>(
  columns: ColumnDefinition<TRow>[],
): Record<string, boolean> {
  const vis: Record<string, boolean> = {}
  for (const c of columns) {
    vis[c.id] = !c.defaultHidden
  }
  return vis
}

export function columnFiltersToChipState(
  columnFilters: { id: string; value: unknown }[],
): Record<string, string[] | null> {
  const out: Record<string, string[] | null> = {}
  for (const f of columnFilters) {
    if (Array.isArray(f.value) && f.value.length > 0) {
      out[f.id] = f.value as string[]
    }
  }
  return out
}

export function chipStateToColumnFilters(
  chips: Record<string, string[] | null>,
): { id: string; value: string[] }[] {
  return Object.entries(chips)
    .filter(([, v]) => v && v.length > 0)
    .map(([id, value]) => ({ id, value: value! }))
}
